"use client";

import { c as _c } from "react/compiler-runtime";
import type { ColumnDefinition } from "@xefi/x-react/datagrid";
import { useTranslation } from "react-i18next";
import type { TelecollectionField, TelecollectionModel } from "../../../../domain/models/Telecollection";
import { CellTooltip } from "../common/CellTooltip";
import { formatPaymentDate, formatPaymentDateTime, formatStringAmount, formatText } from "../common/paymentsFormat";
type CellKind = "text" | "amount" | "date" | "datetime" | "boolean" | "subsidiary";
type TelecollectionColumnConfig = {
  field: TelecollectionField;
  kind: CellKind;
  sortable: boolean;
  className?: string;
};
export const TELECOLLECTION_COLUMNS: TelecollectionColumnConfig[] = [{
  field: "transaction_at",
  kind: "datetime",
  sortable: true,
  className: "w-[14%]"
}, {
  field: "authorization_number",
  kind: "text",
  sortable: true,
  className: "w-[10%]"
},
// Le site vient du libellé porté par la télécollecte, pas de la relation
// résolue à l'import (souvent absente).
{
  field: "site_label",
  kind: "text",
  sortable: true,
  className: "w-[15%]"
}, {
  field: "amount",
  kind: "amount",
  sortable: true,
  className: "w-[10%]"
}, {
  field: "application_name",
  kind: "text",
  sortable: true,
  className: "w-[15%]"
}, {
  field: "deposit_number",
  kind: "text",
  sortable: true,
  className: "w-[11%]"
}, {
  field: "tpv",
  kind: "text",
  sortable: true,
  className: "w-[12%]"
}, {
  field: "is_test",
  kind: "boolean",
  sortable: true
}, {
  field: "subsidiary",
  kind: "subsidiary",
  sortable: false
}, {
  field: "server_at",
  kind: "datetime",
  sortable: true
}, {
  field: "deposited_at",
  kind: "date",
  sortable: true
}, {
  field: "brand",
  kind: "text",
  sortable: true
}, {
  field: "branch",
  kind: "text",
  sortable: true
}, {
  field: "transaction_type",
  kind: "text",
  sortable: true
}, {
  field: "transaction_status",
  kind: "text",
  sortable: true
}, {
  field: "merchant_contract",
  kind: "text",
  sortable: true
}, {
  field: "network",
  kind: "text",
  sortable: true
}, {
  field: "currency",
  kind: "text",
  sortable: true
}, {
  field: "transaction_channel",
  kind: "text",
  sortable: true
}, {
  field: "authorization",
  kind: "text",
  sortable: true
}, {
  field: "mode",
  kind: "text",
  sortable: true
}, {
  field: "worldline_transaction_id",
  kind: "text",
  sortable: true
}, {
  field: "order_reference",
  kind: "text",
  sortable: true
}, {
  field: "card_entry_condition",
  kind: "text",
  sortable: true
}, {
  field: "verification_method",
  kind: "text",
  sortable: true
}, {
  field: "card_number",
  kind: "text",
  sortable: true
}, {
  field: "cashier",
  kind: "text",
  sortable: true
}, {
  field: "initial_transaction_id",
  kind: "text",
  sortable: true
}, {
  field: "receipt_number",
  kind: "text",
  sortable: true
}, {
  field: "deposit_status",
  kind: "text",
  sortable: true
}, {
  field: "acceptance_method",
  kind: "text",
  sortable: true
}, {
  field: "trace_number",
  kind: "text",
  sortable: true
}, {
  field: "forcing_type",
  kind: "text",
  sortable: true
}, {
  field: "p2pe",
  kind: "text",
  sortable: true
}, {
  field: "card_type",
  kind: "text",
  sortable: true
}, {
  field: "card_category",
  kind: "text",
  sortable: true
}, {
  field: "issuing_bank",
  kind: "text",
  sortable: true
}, {
  field: "issuing_country",
  kind: "text",
  sortable: true
}, {
  field: "acquirer_id",
  kind: "text",
  sortable: true
}, {
  field: "aid",
  kind: "text",
  sortable: true
}, {
  field: "processing_status",
  kind: "text",
  sortable: true
}, {
  field: "bin",
  kind: "text",
  sortable: true
}, {
  field: "issuer_transaction_id",
  kind: "text",
  sortable: true
}, {
  field: "blitz_mode",
  kind: "text",
  sortable: true
}, {
  field: "blitz_decision_code",
  kind: "text",
  sortable: true
}, {
  field: "fnci_response",
  kind: "text",
  sortable: true
}, {
  field: "rejection_reason",
  kind: "text",
  sortable: true
}, {
  field: "session_number",
  kind: "text",
  sortable: true
}, {
  field: "guarantor_response",
  kind: "text",
  sortable: true
}, {
  field: "cheque_number",
  kind: "text",
  sortable: true
}, {
  field: "account_number",
  kind: "text",
  sortable: true
}, {
  field: "fnci_guarantee_view",
  kind: "text",
  sortable: true
}, {
  field: "degraded_mode_indicator",
  kind: "text",
  sortable: true
}, {
  field: "authorized_forcing_code",
  kind: "text",
  sortable: true
}, {
  field: "tpv_on_us",
  kind: "text",
  sortable: true
}, {
  field: "authorization_code",
  kind: "text",
  sortable: true
}, {
  field: "aggregated_deposit_number",
  kind: "text",
  sortable: true
}, {
  field: "payment_option",
  kind: "text",
  sortable: true
}, {
  field: "payment_option_code",
  kind: "text",
  sortable: true
}, {
  field: "worldline_order_id",
  kind: "text",
  sortable: true
}, {
  field: "platform_response_code",
  kind: "text",
  sortable: true
}, {
  field: "additional_services",
  kind: "text",
  sortable: true
}, {
  field: "contactless_type",
  kind: "text",
  sortable: true
}, {
  field: "bank_code",
  kind: "text",
  sortable: true
}, {
  field: "case_number",
  kind: "text",
  sortable: true
}, {
  field: "network_product",
  kind: "text",
  sortable: true
}, {
  field: "donation_amount",
  kind: "amount",
  sortable: true
}, {
  field: "total_amount",
  kind: "amount",
  sortable: true
}, {
  field: "other_amount",
  kind: "amount",
  sortable: true
}, {
  field: "authorized_amount",
  kind: "amount",
  sortable: true
}];
export const DEFAULT_TELECOLLECTION_FIELDS: TelecollectionField[] = ["transaction_at", "authorization_number", "site_label", "amount", "application_name", "deposit_number", "tpv"];
const renderCell = (config: TelecollectionColumnConfig, row: TelecollectionModel, labels: {
  yes: string;
  no: string;
}): string => {
  const value = row[config.field];
  switch (config.kind) {
    case "amount":
      return `${formatStringAmount(value)} €`;
    case "date":
      return formatPaymentDate(value);
    case "datetime":
      return formatPaymentDateTime(value);
    case "boolean":
      return value ? labels.yes : labels.no;
    case "subsidiary":
      return formatText(row.subsidiary?.name);
    default:
      return formatText(value);
  }
};
export const useTelecollectionsColumns = visibleFields => {
  const $ = _c(5);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t || $[1] !== visibleFields) {
    const labels = {
      yes: t("paymentsReceived.telecollections.yes"),
      no: t("paymentsReceived.telecollections.no")
    };
    let t1;
    if ($[3] !== visibleFields) {
      t1 = config => visibleFields.has(config.field);
      $[3] = visibleFields;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = TELECOLLECTION_COLUMNS.filter(t1).map(config_0 => ({
      header: t(`paymentsReceived.telecollections.columns.${config_0.field}`),
      field: config_0.field,
      sortable: config_0.sortable,
      className: config_0.className ? `${config_0.className} min-w-0` : "min-w-0",
      cell: row => {
        const value = renderCell(config_0, row, labels);
        return <CellTooltip value={value}><span className="block truncate text-xs text-black dark:text-white">{value}</span></CellTooltip>;
      }
    }));
    $[0] = t;
    $[1] = visibleFields;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};